// Shared primitives for the Workflows feature: state styling (mirrors uTask's
// color semantics), badges, JSON viewer, modal, spinner, and a role hook.
//
// Wire format reminder (see services/api.js): responses are snake_case, so read
// run.steps_done / step.action_type / resolution.run_count, etc.
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { modelAccessRecoveryMessage } from '../../utils/modelAccessRecovery';

// ── Tailwind className constants (match Workspaces.jsx idioms) ──────────────
export const inputClass =
  'h-9 w-full rounded-md border border-gray-700 bg-gray-800 px-3 text-sm text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
export const selectClass =
  'h-9 w-full rounded-md border border-gray-700 bg-gray-800 px-3 text-sm text-gray-200 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
export const textareaClass =
  'w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 font-mono text-xs text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
export const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const btnGhost =
  'inline-flex items-center gap-1.5 rounded-md border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-300 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const btnDanger =
  'inline-flex items-center gap-1.5 rounded-md border border-red-900/50 bg-red-900/20 px-3 py-1.5 text-sm font-medium text-red-300 hover:bg-red-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const btnCancel =
  'rounded-md px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800 transition-colors';
export const cardClass =
  'rounded-lg border border-gray-700 bg-gray-900 p-5';

// ── State styling ───────────────────────────────────────────────────────────
// Covers uTask task states, resolution states AND step states in one map so the
// same badge works everywhere. Keyed by exact uppercase state string.
const GREEN = { badge: 'bg-green-900/40 text-green-300 border border-green-800/60', dot: 'bg-green-400' };
const BLUE = { badge: 'bg-blue-900/40 text-blue-300 border border-blue-800/60', dot: 'bg-blue-400' };
const GRAY = { badge: 'bg-gray-700/60 text-gray-300 border border-gray-600/60', dot: 'bg-gray-400' };
const ORANGE = { badge: 'bg-orange-900/40 text-orange-300 border border-orange-800/60', dot: 'bg-orange-400' };
const PURPLE = { badge: 'bg-purple-900/40 text-purple-300 border border-purple-800/60', dot: 'bg-purple-400' };
const RED = { badge: 'bg-red-900/40 text-red-300 border border-red-800/60', dot: 'bg-red-400' };
const AMBER = { badge: 'bg-amber-900/40 text-amber-300 border border-amber-800/60', dot: 'bg-amber-400' };

const STATE_STYLES = {
  // task / resolution states
  TODO: GRAY,
  RUNNING: BLUE,
  DONE: GREEN,
  CANCELLED: ORANGE,
  WONTFIX: AMBER,
  WAITING: PURPLE,
  DELAYED: PURPLE,
  PAUSED: AMBER,
  BLOCKED: RED,
  BLOCKED_BADREQUEST: RED,
  BLOCKED_MAXRETRIES: AMBER,
  ERROR: RED,
  // step states
  TO_RETRY: ORANGE,
  EXPANDED: BLUE,
  PRUNE: GRAY,
  CLIENT_ERROR: RED,
  SERVER_ERROR: RED,
  FATAL_ERROR: RED,
  AFTERRUN_ERROR: RED,
  RESOLVED: GREEN,
  // approval statuses
  pending: AMBER,
  approved: GREEN,
  rejected: RED,
  resolved: GREEN,
};

export function stateStyle(state) {
  if (!state) return GRAY;
  return STATE_STYLES[state] || STATE_STYLES[String(state).toUpperCase()] || GRAY;
}

export function WorkflowStateBadge({ state, className = '' }) {
  const s = stateStyle(state);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${s.badge} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {state || 'UNKNOWN'}
    </span>
  );
}

// ── JSON viewer ───────────────────────────────────────────────────────────
export function JsonBlock({ value, className = '', maxHeight = '20rem' }) {
  let text;
  if (value === null || value === undefined) {
    text = String(value);
  } else if (typeof value === 'string') {
    text = value;
  } else {
    try {
      text = JSON.stringify(value, null, 2);
    } catch {
      text = String(value);
    }
  }
  return (
    <pre
      className={`overflow-auto rounded-md border border-gray-800 bg-gray-950 p-3 font-mono text-xs leading-relaxed text-gray-300 whitespace-pre-wrap break-words ${className}`}
      style={{ maxHeight }}
    >
      {text}
    </pre>
  );
}

// ── Modal (framer-motion overlay, click-outside to close) ───────────────────
export function Modal({ title, onClose, children, footer, maxWidth = 'max-w-2xl' }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className={`flex max-h-[90vh] w-full ${maxWidth} flex-col rounded-xl border border-gray-800 bg-gray-900 shadow-2xl`}
      >
        <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-100">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-gray-800 px-5 py-3">
            {footer}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-400">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-600 border-t-blue-400" />
      {label || 'Loading…'}
    </div>
  );
}

export function EmptyState({ children }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-800 bg-gray-900/40 py-12 text-center text-sm text-gray-500">
      {children}
    </div>
  );
}

// ── Role hook ───────────────────────────────────────────────────────────────
// The gateway is the source of truth for RBAC; this only gates UI affordances
// to match. A "manager" (admin / tenant reseller / org admin) may delete runs
// and moderate others' comments/approvals — mirrors workflowService.isManager.
export function useWorkflowRole() {
  const { user } = useAuth();
  return useMemo(() => {
    const role = user?.role || 'user';
    const orgRole = user?.org_role || user?.orgRole || null;
    const isAdmin = role === 'admin';
    const isManager = role === 'admin' || role === 'tenant' || orgRole === 'admin';
    return { role, orgRole, isAdmin, isManager, userId: user?.id || null };
  }, [user]);
}

// ── Misc helpers ──────────────────────────────────────────────────────────
export function relTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  // uTask serializes "unset" timestamps as the zero time (0001-01-01T00:00:00Z);
  // anything at/before the epoch is a sentinel, not a real time → show as '—'.
  if (d.getTime() <= 0) return '—';
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const min = 60_000, hr = 3_600_000, day = 86_400_000;
  if (abs < min) return 'just now';
  if (abs < hr) return `${Math.round(abs / min)}m ${diff < 0 ? 'from now' : 'ago'}`;
  if (abs < day) return `${Math.round(abs / hr)}h ${diff < 0 ? 'from now' : 'ago'}`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d ${diff < 0 ? 'from now' : 'ago'}`;
  return d.toLocaleDateString();
}

export function progressPct(done, total) {
  const d = Number(done) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.round((d / t) * 100));
}

// Pull a usable message out of an axios error.
export function errMsg(err, fallback = 'Something went wrong') {
  return modelAccessRecoveryMessage(err, fallback);
}

// Owner / tenant display (gateway enriches rows with these snake_case fields).
export function ownerLabel(row) {
  return row?.owner_email || row?.owner_name || (row?.user_id ? `${row.user_id.slice(0, 8)}…` : '—');
}
export function tenantLabel(row) {
  return row?.tenant_slug || row?.tenant_name || (row?.tenant_id ? `${row.tenant_id.slice(0, 8)}…` : '—');
}
